import React from 'react'
import {
  Box,
  Grid,
  Button,
  TextField,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Autocomplete,
  Stack,
  Divider,
  Card,
  CardContent,
} from '@mui/material'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { getRequest, postRequest, putRequest } from 'Includes/api/apicall'
import { GET_URL, POST_URL, PUT_URL } from 'Includes/urls'
import { VisibilityOutlined, Apps, Android, Apple } from '@mui/icons-material'
import Swal from 'sweetalert2'

// API helpers never throw: on failure they show an error popup and resolve with undefined.
const isOk = (res) => Boolean(res) && (res.status === 200 || res.status === 201)

const emptyForm = () => ({
  app_host: '',
  version_name: '',
  app_type: 'student',
  device_type: 'android',
  is_current_version: true,
  need_force_update: false,
})

function AppVersionPage(props) {
  const location = useLocation()
  const navigate = useNavigate()
  const detailId = location?.state?.detail
  const isEdit = Boolean(detailId)

  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [form, setForm] = React.useState(emptyForm)
  const [errors, setErrors] = React.useState({})
  const [existingPackages, setExistingPackages] = React.useState([])

  // Load existing packages for autocomplete
  React.useEffect(() => {
    getRequest(`${GET_URL.app_version.api}packages/`, {}, props)
      .then((res) => {
        if (res && res.status === 200 && Array.isArray(res.data?.data)) {
          setExistingPackages(res.data.data.filter(Boolean))
        }
      })
      .catch((err) => console.error('Failed to load packages:', err))
  }, [props])

  const loadDetail = (id) => {
    if (!id) return
    setLoading(true)
    getRequest(`${GET_URL.app_version.api}${id}/`, {}, props)
      .then((res) => {
        if (!isOk(res)) return
        const d = res.data?.data || res.data
        if (!d) return
        setForm({
          app_host: d.app_host || '',
          version_name: d.version_name || '',
          app_type: d.app_type || 'student',
          device_type: d.device_type || 'android',
          is_current_version: Boolean(d.is_current_version),
          need_force_update: Boolean(d.need_force_update),
        })
      })
      .catch((err) => {
        console.error('Failed to load version details:', err)
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load details' })
      })
      .finally(() => setLoading(false))
  }

  React.useEffect(() => {
    if (isEdit) {
      loadDetail(detailId)
    } else {
      setForm(emptyForm())
    }
  }, [detailId, isEdit])

  const validate = () => {
    const next = {}
    if (!String(form.app_host || '').trim()) {
      next.app_host = 'Package Name / App Host is required'
    } else if (!/^[a-zA-Z0-9._-]+$/.test(form.app_host.trim())) {
      next.app_host = 'Package name can only contain letters, numbers, dots, and hyphens'
    }

    if (!String(form.version_name || '').trim()) {
      next.version_name = 'Version number is required'
    } else if (!/^\d+(\.\d+)*$/.test(form.version_name.trim())) {
      next.version_name = 'Version should follow semantic format (e.g. 1.0.0, 1.2.4)'
    }

    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return
    setSaving(true)
    try {
      const payload = {
        app_host: String(form.app_host || '').trim(),
        version_name: String(form.version_name || '').trim(),
        app_type: form.app_type,
        device_type: form.device_type,
        is_current_version: Boolean(form.is_current_version),
        need_force_update: Boolean(form.need_force_update),
      }
      if (isEdit) {
        const res = await putRequest(`${PUT_URL.app_version.api}${detailId}/`, payload, props)
        if (!isOk(res)) return
        Swal.fire({
          icon: 'success',
          title: 'Version Updated',
          text: `Updated version ${payload.version_name} for ${payload.app_host}`,
          timer: 1500,
          showConfirmButton: false,
        })
      } else {
        const res = await postRequest(POST_URL.app_version.api, payload, props)
        if (!isOk(res)) return
        Swal.fire({
          icon: 'success',
          title: 'Saved Successfully',
          text: `Added version ${payload.version_name} for ${payload.app_host}`,
          timer: 1500,
          showConfirmButton: false,
        })
      }
      navigate('/app-version/list')
    } catch (err) {
      console.error('Error saving app version:', err)
      Swal.fire({
        icon: 'error',
        title: 'Error Saving',
        text: err?.response?.data?.Reason || err?.message || 'Could not save version.',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '10px',
                  bgcolor: '#4680FF',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Apps sx={{ fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  {isEdit ? 'Edit App Version' : 'Add App Version / Package'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isEdit
                    ? `Update release settings for package ${form.app_host}`
                    : 'Register a new package or publish an app version release.'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} textAlign="right">
            <Button
              variant="outlined"
              component={Link}
              to="/app-version/list"
              startIcon={<VisibilityOutlined />}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              View All Versions & Packages
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3, border: '1px solid #E2E8F0', maxWidth: 800, mx: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            {/* Package / App Host */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Package Name / App Host Identifier *
              </Typography>
              <Autocomplete
                freeSolo
                options={existingPackages}
                value={form.app_host}
                onInputChange={(event, newInputValue) => {
                  setForm({ ...form, app_host: newInputValue })
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="e.g. com.sms_react_native or com.schoolname.student"
                    error={Boolean(errors.app_host)}
                    helperText={errors.app_host || 'Unique Android package or iOS bundle ID configured in mobile app.'}
                  />
                )}
              />
            </Box>

            {/* Version Number */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Version Number *
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g. 1.0.49 or 2.0.1"
                value={form.version_name}
                onChange={(e) => setForm({ ...form, version_name: e.target.value })}
                error={Boolean(errors.version_name)}
                helperText={errors.version_name || 'Semantic version string compared against mobile client installed version.'}
              />
            </Box>

            {/* Platform & App Type */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  App Type *
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={form.app_type}
                    onChange={(e) => setForm({ ...form, app_type: e.target.value })}
                  >
                    <MenuItem value="student">Student App</MenuItem>
                    <MenuItem value="staff">Staff App</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Platform *
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={form.device_type}
                    onChange={(e) => setForm({ ...form, device_type: e.target.value })}
                  >
                    <MenuItem value="android">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Android sx={{ color: '#16A34A', fontSize: 18 }} />
                        Android
                      </Box>
                    </MenuItem>
                    <MenuItem value="ios">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Apple sx={{ color: '#0F172A', fontSize: 18 }} />
                        iOS
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Release Settings */}
            <Card elevation={0} sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 2 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: '#334155' }}>
                  Release Deployment Flags
                </Typography>
                <Stack spacing={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.is_current_version}
                        onChange={(e) => setForm({ ...form, is_current_version: e.target.checked })}
                        color="success"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Set as Current Active Version
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          When checked, any client device requesting update status for this package and platform will target this release.
                        </Typography>
                      </Box>
                    }
                  />

                  <Divider />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.need_force_update}
                        onChange={(e) => setForm({ ...form, need_force_update: e.target.checked })}
                        color="error"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Force Update Required
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Prompts users with a mandatory update screen that blocks app usage until updated from app store.
                        </Typography>
                      </Box>
                    }
                  />
                </Stack>
              </CardContent>
            </Card>

            {/* Submit / Cancel Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2 }}>
              <Button
                variant="outlined"
                component={Link}
                to="/app-version/list"
                disabled={saving}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  bgcolor: '#4680FF',
                  px: 4,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#2563EB' },
                }}
              >
                {saving ? <CircularProgress size={22} color="inherit" /> : isEdit ? 'Update Version' : 'Save Version'}
              </Button>
            </Box>
          </Stack>
        )}
      </Paper>
    </Box>
  )
}

export default AppVersionPage
